import React from "react";
import { Helmet } from "react-helmet";
import {
  Smartphone,
  ShieldCheck,
  RefreshCcw,
  WifiOff,
  Code,
  Rocket,
  Layers,
  Users,
} from "lucide-react";
import SEO from '../../components/SEO';


export default function EnterpriseMobileApplications() {
  const features = [
    {
      title: "Custom Android & iOS App Development",
      desc: "We build native Android and iOS apps tailored to your business goals with optimized UX and performance.",
      icon: <Smartphone size={30} color="#0a3c6d" />,
    },
    {
      title: "Enterprise-Grade Security",
      desc: "Secure logins, encrypted data, and role-based access ensure your app meets industry security standards.",
      icon: <ShieldCheck size={30} color="#0a3c6d" />,
    },
    {
      title: "Cross-Platform Development",
      desc: "Leverage React Native or Flutter to develop once and deploy on both Android and iOS seamlessly.",
      icon: <RefreshCcw size={30} color="#0a3c6d" />,
    },
    {
      title: "Offline Access with Auto Sync",
      desc: "Enable your workforce to work even without internet and auto-sync when reconnected.",
      icon: <WifiOff size={30} color="#0a3c6d" />,
    },
    {
      title: "Robust API Integrations",
      desc: "We integrate secure REST & GraphQL APIs to connect your mobile app with your systems in real time.",
      icon: <Code size={30} color="#0a3c6d" />,
    },
  ];

  const benefits = [
    {
      title: "Fast Deployment",
      icon: <Rocket size={24} color="#0a3c6d" />,
      desc: "Accelerate time to market with rapid development cycles using agile methodologies.",
    },
    {
      title: "Modular Architecture",
      icon: <Layers size={24} color="#0a3c6d" />,
      desc: "Easily scalable and maintainable code architecture for long-term enterprise solutions.",
    },
    {
      title: "User-Centric Design",
      icon: <Users size={24} color="#0a3c6d" />,
      desc: "Intuitive, mobile-first UI/UX that improves adoption and reduces training time.",
    },
  ];

  return (
      <>
    <SEO title="Enterprise Moblie Application" />
    <div className="px-4 py-10 lg:py-20 max-w-7xl mx-auto text-gray-700">
      <Helmet>
        <title>Enterprise Mobile Applications | AKHSoft</title>
        <meta
          name="description"
          content="AKHSoft offers secure and scalable enterprise mobile applications for Android, iOS, and cross-platform. Explore our tailored mobile development services now."
        />
        <link
          rel="canonical"
          href="https://akhsoft.com/software-development/enterprise-mobile-applications"
        />
      </Helmet>

      {/* Header Section */}
      <h1 className="text-4xl font-bold text-center text-[#0a3c6d] mb-6">
        Enterprise Mobile Applications
      </h1>
      <p className="text-center text-lg max-w-3xl mx-auto text-gray-600 mb-12">
        AKHSoft empowers businesses with scalable, secure, and high-performing mobile solutions.
        Whether you need a native iOS/Android app or a cross-platform application, our team delivers
        enterprise-grade mobility to streamline your operations and enhance productivity.
      </p>

      {/* Features Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
        {features.map((item, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl p-6 shadow hover:shadow-lg transition duration-300 text-center"
          >
            <div className="flex justify-center items-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
              {item.icon}
            </div>
            <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
            <p className="text-gray-600">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Benefits Section */}
      <h2 className="text-3xl font-bold text-center text-[#0a3c6d] mb-8">Why Choose AKHSoft?</h2>
      <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
        With a focus on business outcomes, our development team at AKHSoft ensures mobile applications
        are efficient, secure, scalable, and user-centric.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        {benefits.map((b, idx) => (
          <div
            key={idx}
            className="text-center bg-gray-50 rounded-xl p-6 shadow-sm hover:shadow-md transition duration-300"
          >
            <div className="flex justify-center items-center w-12 h-12 mx-auto bg-blue-100 rounded-full mb-3">
              {b.icon}
            </div>
            <h4 className="text-lg font-bold mb-1">{b.title}</h4>
            <p className="text-gray-600 text-sm">{b.desc}</p>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-[#0a3c6d] text-white rounded-2xl py-12 px-6 text-center">
        <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Business?</h3>
        <p className="mb-6 max-w-xl mx-auto text-lg">
          Let AKHSoft be your trusted partner in building secure, scalable, and smart enterprise mobile applications.
        </p>
        <a
          href="/contact-us"
          className="inline-block bg-white text-[#0a3c6d] font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition"
        >
          Get a Free Consultation
        </a>
      </div>
    </div>
    </>
  );
}
